import React, { useEffect, useRef } from "react";
import { Container, Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiX, FiSend, FiEye, FiDownload } from "react-icons/fi";
import type { Row as TanRow } from "@tanstack/react-table";

import ReportTreeTable, { type ReportMetricRow } from "@core/components/ReportTreeTable";
import IncomePivotTable from "@core/reports/components/IncomePivotTable";
import FinanceStatisticsTabs, { type FinanceTabKey } from "../components/FinanceStatisticsTabs";
import FinanceStatisticsView from "../components/FinanceStatisticsView";
import ReportPreviewModal from "@core/reports/components/ReportPreviewModal";
import {
  fetchFinanceStatisticsAsync,
  fetchFinanceCompositeAsync,
  fetchFinanceIncomeBreakdownAsync,
  type DistrictFinanceStatisticsReport,
  type NationFinanceStatisticsReport,
  type DistrictIncomeCompositeReport,
  type NationIncomeBreakdownReport,
  type FinanceStatisticsChurchEntry,
} from "@core/reports/api/fetchFinanceReport";
import {
  buildDistrictIncomePivot,
  buildNationIncomePivot,
  buildDistrictInHousePivot,
  type PivotModel,
} from "@core/reports/adapters/financePivotAdapters";
import {
  statsColumnsFromAggregate,
  statsFooterFromAggregate,
  buildStatsDistrictTree,
  buildStatsNationTree,
  buildGeneralInfoPivot,
} from "@core/reports/adapters/financeTreeAdapters";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import { requestReportApproveAsync } from "@core/reports/api/requestReportApprove";
import { requestDepartmentReportSaveAsync } from "@core/reports/api/requestDepartmentReportSave";
import { requestDepartmentReportSubmitAsync } from "@core/reports/api/requestDepartmentReportSubmit";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import ReportActionsPanel from "@core/components/ReportActionsPanel";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { financeStatsListToViewTrail } from "@core/navigation/pageBreadcrumbTrails";
import NoDepartmentReportNotice from "@core/reports/components/NoDepartmentReportNotice";
import { useDepartmentReportMedia } from "@core/reports/hooks/useDepartmentReportMedia";
import DepartmentReportMediaPanel from "@core/reports/components/DepartmentReportMediaPanel";
import ChildMediaPopover from "@core/reports/components/ChildMediaPopover";
import type { DepartmentReportMediaItem, DepartmentReportDocument, ReportProcessResult } from "@core/reports/api/types";
import {
  canApproveDepartmentReport,
  canRequestChildDepartmentReportReview,
  canRequestDepartmentReportReview,
  canDownloadReport,
  getApprovalNotes,
  getNeedsAttentionReason,
  parseNeedsAttentionDetails,
  getRequestReviewCopy,
  getWorkflowStatusLabel,
  isAncestorOpenForChildReview,
  resolveDepartmentReportDisplayStatus,
  type ReportProcessResults,
} from "@core/reports/utils/reportWorkflow";
import { useLeadershipReportPermissions } from "@core/reports/hooks/useLeadershipReportPermissions";
import { useSelectedReportTag } from "@core/reports/hooks/useSelectedReportTag";
import { buildPrintHeaderFields, buildReportFileBase } from "@core/reports/utils/reportPrintMeta";
import { surfaceDeptActionError } from "@core/reports/utils/surfaceDeptActionError";
import type { FinanceAggregatedSection } from "@core/reports/pdf/exportFinanceAggregatedPdf";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { useAuthStore } from "@features/auth/store/authStore";

type Scope = "district" | "nation";
type StatsData = DistrictFinanceStatisticsReport | NationFinanceStatisticsReport;

export default function FinanceStatisticsAggregatedPage() {
  const { districtId: urlDistrictId, nationId: urlNationId } = useParams<{ districtId?: string; nationId?: string }>();
  const [searchParams] = useSearchParams();

  const scope: Scope = urlNationId ? "nation" : "district";
  const entityId = Number(urlNationId ?? urlDistrictId) || 0;

  const { departmentId, reportTagId } = parseReportViewSearch(searchParams);

  // Tab 3 (statistics)
  const [stats, setStats] = React.useState<StatsData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Tabs 1 & 2 (income) — lazy
  const [income, setIncome] = React.useState<DistrictIncomeCompositeReport | NationIncomeBreakdownReport | null>(null);
  const [incomeLoading, setIncomeLoading] = React.useState(false);
  const [incomeError, setIncomeError] = React.useState<string | null>(null);
  const incomeRequested = useRef(false);

  const [savingDraft, setSavingDraft] = React.useState(false);
  const [submittingDept, setSubmittingDept] = React.useState(false);
  const [validatingDept, setValidatingDept] = React.useState(false);
  const [deptActionError, setDeptActionError] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<ReportProcessResult | null>(null);
  const [deptNotes, setDeptNotes] = React.useState("");
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Child-row request-review modal (district at nation scope; church at district scope)
  const [showRowReviewModal, setShowRowReviewModal] = React.useState(false);
  const [reviewTarget, setReviewTarget] = React.useState<{ kind: "church" | "district"; id: number; name: string; reportId: number | null } | null>(null);
  const [requestingRowReview, setRequestingRowReview] = React.useState(false);
  const [rowReviewError, setRowReviewError] = React.useState<string | null>(null);

  // Parent report request-review modal
  const [showParentReviewModal, setShowParentReviewModal] = React.useState(false);
  const [requestingParentReview, setRequestingParentReview] = React.useState(false);
  const [parentReviewError, setParentReviewError] = React.useState<string | null>(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = React.useState(false);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  // Eyeball preview
  const [previewChurch, setPreviewChurch] = React.useState<FinanceStatisticsChurchEntry | null>(null);

  const canLoad = entityId > 0 && reportTagId > 0;

  const load = React.useCallback(async (): Promise<void> => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFinanceStatisticsAsync<StatsData>(scope, entityId, reportTagId);
      if (result.isSuccess()) setStats(result.getData());
      else { setError(result.getError()?.detail || "Failed to fetch Finance & Statistics report"); setStats(null); }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [scope, entityId, reportTagId, canLoad]);

  React.useEffect(() => { if (canLoad) load(); }, [load, canLoad]);

  const loadIncome = React.useCallback(async (): Promise<void> => {
    if (!canLoad || incomeRequested.current) return;
    incomeRequested.current = true;
    setIncomeLoading(true);
    setIncomeError(null);
    try {
      const result = scope === "nation"
        ? await fetchFinanceIncomeBreakdownAsync<NationIncomeBreakdownReport>(entityId, reportTagId)
        : await fetchFinanceCompositeAsync<DistrictIncomeCompositeReport>("district", entityId, reportTagId);
      if (result.isSuccess()) setIncome(result.getData());
      else setIncomeError(result.getError()?.detail || "Failed to fetch income breakdown");
    } catch (e) {
      setIncomeError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setIncomeLoading(false);
    }
  }, [scope, entityId, reportTagId, canLoad]);

  const handleTabOpen = (key: FinanceTabKey) => {
    if (key === "breakdown" || key === "inhouse") void loadIncome();
  };

  const meta = stats?.meta;
  const submission = meta?.department_submission ?? null;
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const requestReviewCopy = getRequestReviewCopy(
    currentUserId,
    submission?.submitted_by
  );
  const requestReviewLabel = requestReviewCopy.actionLabel;
  const requestReviewNoun = requestReviewCopy.noun;
  const departmentReportId: number | null = submission?.department_report_id ?? null;
  const statusName = resolveDepartmentReportDisplayStatus(
    departmentReportId,
    submission?.report_status_name ?? null
  );
  const processResults = (meta as { process_results?: ReportProcessResults | null } | undefined)
    ?.process_results;
  const needsAttentionReason = getNeedsAttentionReason(statusName, processResults);
  const needsAttentionDetails = parseNeedsAttentionDetails(processResults);
  const approvalNotesDisplay = getApprovalNotes(submission?.approval_notes, processResults);

  const { permissionFlags, actionsVisible } = useLeadershipReportPermissions({
    scope,
    scopeId: entityId,
    departmentId,
    statusName,
    submittedBy: submission?.submitted_by,
    enabled: canLoad,
  });

  const { tagIsOpen, tagStatusName } = useSelectedReportTag({
    districtId: scope === "district" ? entityId || undefined : undefined,
    nationId: scope === "nation" ? entityId || undefined : undefined,
    reportTagId,
    enabled: canLoad,
  });

  const canEdit = permissionFlags.fieldsEditable && tagIsOpen;
  const { showSave, showSubmit, showValidate, showApprove, showUploadMedia, showReview } = permissionFlags;
  const showSaveAction = showSave && tagIsOpen;
  const showSubmitAction = showSubmit && tagIsOpen;
  const showUploadMediaAction = showUploadMedia && tagIsOpen;
  const canRemoveMedia = canEdit && permissionFlags.canRemoveMedia;
  const showRowReview = showReview && actionsVisible;
  const parentOpenForChildReview = isAncestorOpenForChildReview(departmentReportId, statusName);

  useEffect(() => {
    if (savingDraft || submittingDept) return;
    setDeptNotes(String(submission?.notes ?? ""));
  }, [submission?.notes, submission, savingDraft, submittingDept]);

  React.useEffect(() => {
    setDeptActionError(null);
    setValidationResult(null);
  }, [entityId, departmentId, reportTagId]);

  const existingMedia = React.useMemo<DepartmentReportMediaItem[]>(
    () => (Array.isArray(meta?.media) ? (meta?.media as DepartmentReportMediaItem[]) : []),
    [meta]
  );
  const mediaRequirements = React.useMemo<DepartmentReportDocument[]>(() => {
    const value = meta?.documents;
    return Array.isArray(value) ? value : [];
  }, [meta]);

  const media = useDepartmentReportMedia({
    scope,
    entityId,
    departmentId,
    reportTagId,
    notes: deptNotes,
    departmentReportId,
    existingMedia,
    editable: showUploadMediaAction,
    onChanged: async () => { await load(); },
  });

  React.useEffect(() => {
    media.clearUploadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, departmentId, reportTagId]);

  // Index church entries (with their 4 sections) for the eyeball.
  const churchEntryById = React.useMemo(() => {
    const map = new Map<number, FinanceStatisticsChurchEntry>();
    if (!stats) return map;
    if ("churches" in stats) for (const c of stats.churches ?? []) map.set(c.church_id, c);
    else if ("districts" in stats) for (const d of stats.districts ?? []) for (const c of d.churches ?? []) map.set(c.church_id, c);
    return map;
  }, [stats]);

  const openPreview = (churchId: number) => {
    const church = churchEntryById.get(churchId);
    if (church) setPreviewChurch(church);
  };

  // ── Income pivots (Tabs 1 & 2) ─────────────────────────────────────────────
  const incomePivot: PivotModel | null = React.useMemo(() => {
    if (!income) return null;
    return scope === "nation"
      ? buildNationIncomePivot(income as NationIncomeBreakdownReport)
      : buildDistrictIncomePivot(income as DistrictIncomeCompositeReport);
  }, [income, scope]);

  const inHousePivot: PivotModel | null = React.useMemo(() => {
    if (scope !== "district" || !income) return null;
    return buildDistrictInHousePivot(income as DistrictIncomeCompositeReport);
  }, [income, scope]);

  // ── Statistics tables (Tab 3) ──────────────────────────────────────────────
  const agg = stats?.aggregated;
  const districtChurches = stats && "churches" in stats ? stats.churches : undefined;
  const nationDistricts = stats && "districts" in stats ? stats.districts : undefined;

  const giPivot: PivotModel | null = React.useMemo(() => {
    if (!agg) return null;
    return buildGeneralInfoPivot(agg, scope, districtChurches, nationDistricts as any);
  }, [agg, scope, districtChurches, nationDistricts]);

  const buildTree = React.useCallback(
    (kind: "nation_office" | "membership"): ReportMetricRow[] => {
      if (!stats) return [];
      return "districts" in stats
        ? buildStatsNationTree(stats.districts, kind)
        : buildStatsDistrictTree(stats.churches, kind);
    },
    [stats]
  );

  // ── Workflow handlers (own report) ─────────────────────────────────────────
  const ownArgs = scope === "nation"
    ? { scope: "nation" as const, nationId: entityId }
    : { scope: "district" as const, districtId: entityId };

  const handleSaveDraft = async () => {
    if (!canLoad) return;
    setDeptActionError(null); setSavingDraft(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSaveAsync({ ...ownArgs, departmentId, reportTagId, notes: deptNotes, existingMedia: media.captionPatches } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to save draft.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to save draft.",
        setDeptActionError
      );
    }
    finally { setSavingDraft(false); }
  };

  const handleSubmitDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null); setSubmittingDept(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSubmitAsync({ ...ownArgs, departmentId, reportTagId, notes: deptNotes, existingMedia: media.captionPatches } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to submit report.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to submit report.",
        setDeptActionError
      );
    }
    finally { setSubmittingDept(false); }
  };

  const handleValidateDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null); setValidatingDept(true);
    try {
      const result = await processDepartmentReportAsync({ ...ownArgs, departmentId, reportTagId } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to validate report.");
      setValidationResult(result.getData()); await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to validate report.",
        setDeptActionError
      );
    }
    finally { setValidatingDept(false); }
  };

  const handleApprove = async () => {
    if (!canLoad || !departmentReportId) { setApproveError("Missing department report id. Cannot approve report."); return; }
    setApproving(true); setApproveError(null);
    try {
      const result = await requestReportApproveAsync({ ...ownArgs, departmentId, departmentReportId, notes: approveNotes ?? "" } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to approve report.");
      setShowApproveModal(false); await load();
    } catch (e) { setApproveError(e instanceof Error ? e.message : "Failed to approve report."); }
    finally { setApproving(false); }
  };

  const handleRowRequestReview = async ({ reason }: { reason: string }) => {
    if (!reviewTarget?.reportId) {
      setRowReviewError(`Missing department report id for the selected ${reviewTarget?.kind ?? "row"}.`);
      return;
    }
    setRequestingRowReview(true);
    setRowReviewError(null);
    try {
      const result = await requestReportReviewAsync(
        reviewTarget.kind === "district"
          ? {
              scope: "district",
              districtId: reviewTarget.id,
              departmentId,
              departmentReportId: reviewTarget.reportId,
              notes: reason.slice(0, 255),
            }
          : {
              scope: "church",
              churchId: reviewTarget.id,
              departmentId,
              departmentReportId: reviewTarget.reportId,
              notes: reason.slice(0, 255),
            }
      );
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowRowReviewModal(false);
      await load();
    } catch (e) {
      setRowReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingRowReview(false);
    }
  };

  const handleParentRequestReview = async ({ reason }: { reason: string }) => {
    if (!canLoad || !departmentReportId) {
      setParentReviewError("Missing department report id. Cannot request review.");
      return;
    }
    setRequestingParentReview(true);
    setParentReviewError(null);
    try {
      const result = await requestReportReviewAsync({
        ...ownArgs,
        departmentId,
        departmentReportId: Number(departmentReportId),
        notes: reason.slice(0, 255),
      } as Parameters<typeof requestReportReviewAsync>[0]);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowParentReviewModal(false);
      await load();
    } catch (e) {
      setParentReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingParentReview(false);
    }
  };

  const busy = savingDraft || submittingDept || validatingDept;
  const canApproveNow = canApproveDepartmentReport(statusName);
  const canRequestReviewNow = canRequestDepartmentReportReview(statusName);
  const canDownload = canDownloadReport(statusName);

  const [downloadingPage, setDownloadingPage] = React.useState(false);
  const [downloadingPreview, setDownloadingPreview] = React.useState(false);

  const handleDownloadPage = async () => {
    setDownloadingPage(true);
    try {
      // Financial Breakdown / In-House pivots need the lazy-loaded income data.
      let incomeData = income;
      if (!incomeData) {
        const res = scope === "nation"
          ? await fetchFinanceIncomeBreakdownAsync<NationIncomeBreakdownReport>(entityId, reportTagId)
          : await fetchFinanceCompositeAsync<DistrictIncomeCompositeReport>("district", entityId, reportTagId);
        if (res.isSuccess()) { incomeData = res.getData(); setIncome(incomeData); }
      }
      const incomePivotForPdf = incomeData
        ? (scope === "nation"
            ? buildNationIncomePivot(incomeData as NationIncomeBreakdownReport)
            : buildDistrictIncomePivot(incomeData as DistrictIncomeCompositeReport))
        : null;
      const inHousePivotForPdf = scope === "district" && incomeData
        ? buildDistrictInHousePivot(incomeData as DistrictIncomeCompositeReport)
        : null;

      const fields = buildPrintHeaderFields(meta, scope);
      const departmentName = fields.departmentName !== "—" ? fields.departmentName : "Finance & Statistics";
      const title = `${departmentName} Report`;
      const nameHeader = scope === "nation" ? "District" : "Church";
      const fileName = buildReportFileBase(fields, "Finance & Statistics Report");

      const sections: FinanceAggregatedSection[] = [];
      if (giPivot) sections.push({ kind: "pivot", title: "General Information", nameHeader: "Item", model: giPivot });
      if (agg) {
        sections.push({ kind: "tree", title: "Monthly Membership Analysis", nameHeader, columnsSpec: statsColumnsFromAggregate(agg, "membership"), tree: buildTree("membership"), totals: statsFooterFromAggregate(agg, "membership") });
        sections.push({ kind: "tree", title: "Finances to the Nation", nameHeader, columnsSpec: statsColumnsFromAggregate(agg, "nation_office"), tree: buildTree("nation_office"), totals: statsFooterFromAggregate(agg, "nation_office") });
      }
      if (incomePivotForPdf) sections.push({ kind: "pivot", title: "Financial Breakdown", nameHeader: "Item", model: incomePivotForPdf });
      if (inHousePivotForPdf) sections.push({ kind: "pivot", title: "District In-House Memo", nameHeader: "Item", model: inHousePivotForPdf });

      const { exportFinanceAggregatedPdf } = await import("@core/reports/pdf/exportFinanceAggregatedPdf");
      await exportFinanceAggregatedPdf({
        fields,
        title,
        statusName,
        fileName,
        sections,
        notes: submission?.notes ?? deptNotes,
        attachments: existingMedia.length > 0
          ? existingMedia.map((item) => ({
              typeName: item.document_name,
              caption: item.description,
              filename: item.filename,
            }))
          : undefined,
      });
    } finally {
      setDownloadingPage(false);
    }
  };

  const handleDownloadPreview = async (entry: FinanceStatisticsChurchEntry) => {
    setDownloadingPreview(true);
    try {
      const data = {
        ...entry,
        meta: {
          church_name: entry.church_name,
          department_name: meta?.department_name,
          report_tag_name: meta?.report_tag_name,
          report_tag_since: meta?.report_tag_since ?? null,
          report_tag_until: meta?.report_tag_until ?? null,
        },
      };
      const { exportFinanceStatisticsPdf } = await import("@core/reports/pdf/exportFinanceStatisticsPdf");
      await exportFinanceStatisticsPdf(data, {
        scope: "church",
        statusName: entry.department_submission?.report_status_name ?? statusName,
        notes: entry.department_submission?.notes ?? null,
      });
    } finally {
      setDownloadingPreview(false);
    }
  };

  // Row name: nation → district Send; district → church eyeball + Send; media popovers.
  const renderRowName = React.useCallback(
    (row: TanRow<ReportMetricRow>) => {
      const original: any = row.original ?? {};
      const isChurchRow = !!original.church_id;
      const isDistrictRow = !!original.district_id && !isChurchRow;
      const name = String(original.name ?? "").trim() || (isChurchRow ? "Church" : "District");
      const childReportId = Number(original.department_report_id ?? 0) || null;
      const childMedia: DepartmentReportMediaItem[] = Array.isArray(original.media) ? original.media : [];
      const churchId = Number(original.church_id ?? 0) || 0;
      const districtId = Number(original.district_id ?? 0) || 0;
      const childStatus = original.report_status_name ?? null;
      const canReviewChild =
        showRowReview &&
        parentOpenForChildReview &&
        !!childReportId &&
        canRequestChildDepartmentReportReview(childStatus);
      const showDistrictReview = scope === "nation" && isDistrictRow && canReviewChild;
      const showChurchReview = scope === "district" && isChurchRow && canReviewChild;
      const reviewDisabled = requestingRowReview || !childReportId;

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          {isChurchRow ? (
            <button
              type="button"
              title="Preview Finance & Statistics"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); openPreview(churchId); }}
              style={iconBtnStyle(false)}
            >
              <FiEye />
            </button>
          ) : null}
          {showDistrictReview ? (
            <button
              type="button"
              title="Request review"
              disabled={reviewDisabled}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setReviewTarget({ kind: "district", id: districtId, name, reportId: childReportId });
                setRowReviewError(null);
                setShowRowReviewModal(true);
              }}
              style={iconBtnStyle(reviewDisabled)}
            >
              <FiSend />
            </button>
          ) : null}
          {showChurchReview ? (
            <button
              type="button"
              title="Request review"
              disabled={reviewDisabled}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setReviewTarget({ kind: "church", id: churchId, name, reportId: childReportId });
                setRowReviewError(null);
                setShowRowReviewModal(true);
              }}
              style={iconBtnStyle(reviewDisabled)}
            >
              <FiSend />
            </button>
          ) : null}
          </div>
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: isChurchRow ? 600 : 800,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>
          {isChurchRow && churchId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover scope="church" entityId={churchId} departmentId={departmentId} departmentReportId={childReportId} media={childMedia} />
            </span>
          ) : null}
          {isDistrictRow && districtId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover scope="district" entityId={districtId} departmentId={departmentId} departmentReportId={childReportId} media={childMedia} />
            </span>
          ) : null}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestingRowReview, departmentId, churchEntryById, showRowReview, scope, parentOpenForChildReview]
  );

  const renderStatistics = () => (
    <>
      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading Finance &amp; Statistics…</span>
        </div>
      )}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {stats && !loading && agg ? (
        <>
          {giPivot ? <div className="mb-3"><IncomePivotTable title="General Information" model={giPivot} /></div> : null}

          <Card className="mb-3">
            <Card.Header className="fw-semibold">Monthly Membership Analysis</Card.Header>
            <Card.Body className="p-0">
              <ReportTreeTable
                columnsSpec={statsColumnsFromAggregate(agg, "membership")}
                data={buildTree("membership")}
                defaultExpanded={false}
                totals={statsFooterFromAggregate(agg, "membership")}
                totalsLabel="TOTAL"
                renderName={renderRowName}
              />
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="fw-semibold">Finances to the Nation</Card.Header>
            <Card.Body className="p-0">
              <ReportTreeTable
                columnsSpec={statsColumnsFromAggregate(agg, "nation_office")}
                data={buildTree("nation_office")}
                defaultExpanded={false}
                totals={statsFooterFromAggregate(agg, "nation_office")}
                totalsLabel="TOTAL"
                renderName={renderRowName}
              />
            </Card.Body>
          </Card>
        </>
      ) : null}

      {/* Notes + media + workflow on the entity's own GENERAL report */}
      {canLoad && meta && (
        <Card className="mt-4">
          <Card.Body>
            <div className="fw-semibold">Department Report Notes</div>
            <div className="text-muted small mb-3">
              {canEdit ? `Notes are editable while the report is in ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.` : "Notes are read-only once submitted."}
            </div>
            <Form.Group className="mb-0">
              <Form.Control
                ref={notesRef as React.Ref<HTMLTextAreaElement>}
                as="textarea"
                value={deptNotes}
                onChange={(e) => setDeptNotes(e.target.value)}
                disabled={!canEdit || busy}
                placeholder={canEdit ? "Enter notes for this report..." : `Notes can only be edited when ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`}
                style={{ minHeight: "6rem" }}
              />
            </Form.Group>

            <DepartmentReportMediaPanel
              requirements={mediaRequirements}
              existingMedia={existingMedia}
              captionDrafts={media.captionDrafts}
              mediaActionId={media.mediaActionId}
              captionSavingId={media.captionSavingId}
              error={media.error}
              editable={showUploadMediaAction}
              allowRemove={canRemoveMedia}
              busy={busy}
              uploadBusy={media.uploadBusy}
              uploadDraft={media.uploadDraft}
              uploadError={media.uploadError}
              namedFileInputRefs={media.namedFileInputRefs}
              otherFileInputRef={media.otherFileInputRef}
              onNamedFileChange={media.openNamedUpload}
              onOtherFileChange={media.openOtherUpload}
              onUploadDraftCaptionChange={media.setUploadDraftCaption}
              onConfirmUpload={media.confirmUpload}
              onCancelUpload={media.clearUploadDraft}
              onCaptionDraftChange={media.setCaptionDraft}
              onSaveCaption={media.saveCaption}
              onView={media.viewMedia}
              onRemove={media.handleRemoveMedia}
            />
            {approvalNotesDisplay ? (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Approval Notes</div>
                <Form.Control
                  as="textarea"
                  className="text-muted"
                  value={approvalNotesDisplay}
                  disabled
                  readOnly
                  style={{ overflow: "hidden", resize: "none", minHeight: "4rem" }}
                />
              </div>
            ) : null}

            {deptActionError ? <Alert variant="danger" className="mt-3 mb-0">{deptActionError}</Alert> : null}

            <div className="d-flex justify-content-end gap-2 mt-3">
              {actionsVisible && showValidate ? (
                <Button variant="outline-success" className="btn-action" onClick={handleValidateDepartmentReport} disabled={busy || !canLoad}>
                  {validatingDept ? <Spinner animation="border" size="sm" className="me-2" aria-hidden /> : null}
                  {statusName === "NEEDS_ATTENTION" ? "Re-Validate" : "Validate"}
                </Button>
              ) : null}
              {actionsVisible && showSaveAction ? (
              <Button variant="outline-primary" className="btn-action" onClick={handleSaveDraft} disabled={busy || !canLoad || !canEdit}>
                {savingDraft ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Saving…</> : "Save Draft"}
              </Button>
              ) : null}
              {actionsVisible && showSubmitAction ? (
                <Button variant="primary" className="btn-action" onClick={handleSubmitDepartmentReport} disabled={busy || !canLoad}>
                  {submittingDept ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Submitting…</> : <><FiSend className="me-2" aria-hidden />{statusName === "NEEDS_ATTENTION" ? "Resubmit" : "Submit"}</>}
                </Button>
              ) : null}
              {actionsVisible && showReview && canRequestReviewNow && departmentReportId ? (
                <Button
                  variant="outline-primary"
                  className="btn-action"
                  onClick={() => { setParentReviewError(null); setShowParentReviewModal(true); }}
                  disabled={busy || !canLoad || requestingParentReview || approving}
                  title="Return this report for corrections"
                >
                  <FiSend className="me-2" aria-hidden />
                  {requestReviewLabel}
                </Button>
              ) : null}
              {actionsVisible && showApprove && canApproveNow && departmentReportId ? (
                <Button variant="success" className="btn-action" onClick={() => { setApproveNotes(""); setApproveError(null); setShowApproveModal(true); }} disabled={busy || !canLoad}>
                  <FiCheckCircle className="me-2" aria-hidden />Approve Report
                </Button>
              ) : null}
              {canDownload ? (
                <Button variant="primary" className="btn-action" onClick={handleDownloadPage} disabled={busy || !canLoad || downloadingPage} aria-busy={downloadingPage} title="Download this report as a PDF">
                  {downloadingPage ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Generating…</> : <><FiDownload className="me-2" aria-hidden />Download PDF</>}
                </Button>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );

  const renderBreakdown = () => (
    <>
      {incomeLoading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading financial breakdown…</span>
        </div>
      )}
      {incomeError && <Alert variant="danger" className="mb-3">{incomeError}</Alert>}
      {incomePivot && !incomeLoading ? <IncomePivotTable title="Financial Breakdown" model={incomePivot} /> : null}
    </>
  );

  const renderInHouse = () => (
    <>
      {incomeLoading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading district in-house memo…</span>
        </div>
      )}
      {incomeError && <Alert variant="danger" className="mb-3">{incomeError}</Alert>}
      {inHousePivot && !incomeLoading ? <IncomePivotTable title="District In-House Memo" model={inHousePivot} /> : null}
    </>
  );

  const basePath = useEntityBasePath();
  const breadcrumbItems = React.useMemo(
    () =>
      basePath
        ? financeStatsListToViewTrail(basePath, meta?.department_name)
        : [],
    [basePath, meta?.department_name]
  );

  return (
    <Container fluid className="py-4">
      <PageBreadcrumb items={breadcrumbItems} />
      <ReportActionsPanel
        meta={{
          scopeLabel: "",
          scopeName: "",
          departmentName: meta?.department_name ?? "Finance & Statistics",
          reportTagName: meta?.report_tag_name ?? "—",
          reportTagStatusName: tagStatusName,
          statusName,
          submittedAt: submission?.submitted_at ?? null,
          submittedBy: submission?.submitted_by ?? null,
          submittedByName: submission?.submitted_by_name ?? null,
          approvedAt: submission?.approved_at ?? null,
          approvedByName: submission?.approved_by_name ?? null,
          departmentReportId,
        }}
        statusLabel="Status"
        needsAttentionReason={needsAttentionReason}
        needsAttentionDetails={needsAttentionDetails}
        statusNote={
          canEdit
            ? "This aggregated Finance & Statistics report is editable. Use the eyeball to preview a church's full report, or request review on a specific church."
            : "This report is view-only in its current workflow state."
        }
        notices={
          <>
            {!tagIsOpen ? (
              <Alert variant="warning" className="py-2 mb-2 small">
                This reporting period is not open. Saving and submitting are unavailable.
              </Alert>
            ) : null}
            {canLoad && !departmentReportId ? <NoDepartmentReportNotice /> : null}
            {validationResult ? (
              <Alert variant={validationResult.is_valid ? "success" : "warning"} className="py-2 mb-0 small">
                {validationResult.message}
              </Alert>
            ) : null}
          </>
        }
      />

      {!canLoad && !loading && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">Select a reporting period to view the aggregated Finance &amp; Statistics report.</p>
          </Card.Body>
        </Card>
      )}

      {canLoad && (
        <FinanceStatisticsTabs
          showInHouse={scope === "district"}
          onOpen={handleTabOpen}
          renderStatistics={renderStatistics}
          renderBreakdown={renderBreakdown}
          renderInHouse={renderInHouse}
        />
      )}

      {/* Child-row Request Review modal */}
      <RequestReviewModal
        show={showRowReviewModal}
        onHide={() => { if (!requestingRowReview) setShowRowReviewModal(false); }}
        subject={
          <>
            You are requesting review for: <span className="fw-semibold">{reviewTarget?.name ?? (reviewTarget?.kind === "district" ? "District" : "Church")}</span>
          </>
        }
        busy={requestingRowReview}
        error={rowReviewError}
        onConfirm={handleRowRequestReview}
      />

      {/* Parent report Request Review modal */}
      <RequestReviewModal
        show={showParentReviewModal}
        onHide={() => { if (!requestingParentReview) setShowParentReviewModal(false); }}
        title={requestReviewLabel}
        submittedBy={submission?.submitted_by}
        subject={
          <>
            You are requesting {requestReviewNoun} for this {scope === "nation" ? "nation" : "district"} Finance and Statistics department report.
            It will be marked as <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be edited and resubmitted.
          </>
        }
        busy={requestingParentReview}
        error={parentReviewError}
        onConfirm={handleParentRequestReview}
      />

      {/* Approve modal */}
      <Modal show={showApproveModal} onHide={() => { if (!approving) setShowApproveModal(false); }} centered backdrop="static">
        <Modal.Header closeButton={!approving}><Modal.Title>Approve Report</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2">Approving the aggregated report will move the status to <strong>{getWorkflowStatusLabel("APPROVED")}</strong>.</div>
          <Form.Group>
            <Form.Label className="fw-medium">Notes</Form.Label>
            <Form.Control as="textarea" rows={4} value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} disabled={approving} placeholder="Add approval notes (optional)..." />
          </Form.Group>
          {approveError ? <Alert variant="danger" className="mt-3 mb-0">{approveError}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowApproveModal(false)} disabled={approving}><FiX className="me-2" />Cancel</Button>
          <Button variant="primary" onClick={handleApprove} disabled={approving || !departmentReportId || !canApproveNow}>
            {approving ? <Spinner animation="border" size="sm" className="me-2" /> : <FiCheckCircle className="me-2" />}Confirm Approve
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Eyeball preview — full church Finance & Statistics view (from payload) */}
      <ReportPreviewModal
        show={!!previewChurch}
        onHide={() => setPreviewChurch(null)}
        title={previewChurch?.church_name ?? "Finance & Statistics"}
        subtitle={meta?.report_tag_name}
        onDownload={canDownload && previewChurch ? () => handleDownloadPreview(previewChurch) : undefined}
        downloading={downloadingPreview}
      >
        {previewChurch ? <FinanceStatisticsView data={previewChurch} showHeader={false} /> : null}
      </ReportPreviewModal>
    </Container>
  );
}

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    border: "1px solid var(--ntcog-border)",
    borderRadius: "var(--ntcog-radius-sm)",
    background: disabled ? "var(--ntcog-surface)" : "var(--ntcog-input)",
    cursor: disabled ? "not-allowed" : "pointer",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  };
}
